import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { type Incident } from "./types";
import { type ChatTabStage } from "./RestorationRoomChat";
import {
  EXTENDED_STAGE,
  STAGE_META,
  getStageCompletion,
  showsExtendedStage,
} from "./StageTracker";

// Pull a labelled "X:" line out of conversation_notes, e.g. extract the
// "Motivation:" line so the summary can show what the leader's read was.
const extractNoteLine = (
  notes: string | null | undefined,
  label: string,
): string | null => {
  if (!notes) return null;
  const re = new RegExp(`^${label}:\\s*(.+)$`, "im");
  const m = notes.match(re);
  return m ? m[1].trim() : null;
};

const truncate = (s: string, max = 160) =>
  s.length > max ? s.slice(0, max - 1).trimEnd() + "…" : s;

// Per-stage projection of what's been captured. Empty values are
// rendered as "Not captured yet" so the leader can immediately see
// where the gaps are.
type Field = { label: string; value: string | null };
type StageSummary = {
  key: ChatTabStage;
  num: number;
  label: string;
  fields: Field[];
};

// Captured-fields list for a single stage, formatted as data-card chips
// (`{ key, value }`). Used by the chat's "review mode" so a leader who
// revisits a completed stage sees the same projection the summary card
// shows — without re-engaging the step's scripted prompts.
export const buildStageChips = (
  incident: Incident,
  stage: ChatTabStage,
): { key: string; value: string }[] => {
  const stageSummary = buildStageSummaries(incident).find((s) => s.key === stage);
  if (!stageSummary) return [];
  return stageSummary.fields
    .filter((f) => f.value)
    .map((f) => ({ key: f.label, value: f.value as string }));
};

const buildStageSummaries = (incident: Incident): StageSummary[] => {
  const desc = incident.incident_description?.trim() || null;
  const name = incident.student_identifier?.trim() || null;
  const grade = incident.grade_level || null;

  const trigger = extractNoteLine(incident.conversation_notes, "Trigger\\/context");
  const motivation = extractNoteLine(incident.conversation_notes, "Motivation");
  const outcome = extractNoteLine(incident.conversation_notes, "Outcome");
  const teacherFollowUp = extractNoteLine(
    incident.conversation_notes,
    "Teacher follow-up",
  );
  // Most recent handoff line (a single incident may have several over its
  // life). We grep ALL "Handoff note" lines and surface the latest so the
  // summary card reflects the current pause state.
  const lastHandoff = (() => {
    if (!incident.conversation_notes) return null;
    const matches = incident.conversation_notes.match(
      /^Handoff note[^\n]*$/gim,
    );
    if (!matches || matches.length === 0) return null;
    return matches[matches.length - 1].replace(/^Handoff note[^:]*:\s*/i, "").trim();
  })();
  const teacherAsk =
    incident.teacher_outcome_checked?.length
      ? incident.teacher_outcome_checked.join(", ")
      : incident.teacher_requested_outcome?.trim() || null;
  const prior =
    incident.prior_interventions_checked?.length
      ? incident.prior_interventions_checked.join(", ")
      : incident.prior_interventions?.trim() || null;

  const iep =
    incident.iep_504_flag === true
      ? "Yes"
      : extractNoteLine(incident.conversation_notes, "IEP\\/504 status");

  const path = (() => {
    if (!incident.chosen_path) return null;
    switch (incident.chosen_path) {
      case "one_to_one_conversation":
        return "1:1 conversation";
      case "supported_meeting":
        return "Supported meeting";
      case "pause_support":
        return "Pause / regulate first";
      case "documentation_only":
        return "Documentation only";
      default:
        return null;
    }
  })();

  const summaries: StageSummary[] = [
    {
      key: "incident",
      num: 1,
      label: "The Situation",
      fields: [
        {
          label: "Student",
          value: name && grade ? `${name} · Grade ${grade}` : name || (grade ? `Grade ${grade}` : null),
        },
        {
          label: "What happened",
          value: desc ? truncate(desc, 200) : null,
        },
        { label: "IEP / 504 / BIP", value: iep },
      ],
    },
    {
      key: "reflection",
      num: 2,
      label: "Understand",
      fields: [
        { label: "Trigger / context", value: trigger ? truncate(trigger, 200) : null },
        { label: "Prior interventions", value: prior ? truncate(prior, 200) : null },
        { label: "Teacher is asking for", value: teacherAsk ? truncate(teacherAsk, 200) : null },
        { label: "Read on motivation", value: motivation ? truncate(motivation, 200) : null },
      ],
    },
    {
      key: "conversation",
      num: 3,
      label: "The Conversation",
      fields: [
        { label: "Path chosen", value: path },
        {
          label: "Conversation",
          value: incident.conversation_completed ? "Marked complete" : null,
        },
        { label: "Outcome", value: outcome ? truncate(outcome, 200) : null },
      ],
    },
    {
      key: "resolution",
      num: 4,
      label: "Resolution",
      fields: [
        {
          label: "Follow-up plan",
          value: incident.behavior_follow_up?.trim()
            ? truncate(incident.behavior_follow_up.trim(), 200)
            : null,
        },
        {
          label: "Teacher follow-up",
          value: teacherFollowUp ? truncate(teacherFollowUp, 200) : null,
        },
        {
          label: "Parent contact",
          value: incident.parent_contact_type
            ? incident.parent_contact_type === "student_with_adult"
              ? "Student + adult call"
              : "Adult call"
            : null,
        },
        {
          label: "Follow-up date",
          value: incident.follow_up_date || null,
        },
        {
          label: "Handoff note",
          value: lastHandoff ? truncate(lastHandoff, 200) : null,
        },
      ],
    },
  ];

  if (showsExtendedStage(incident)) {
    summaries.push({
      key: EXTENDED_STAGE.key,
      num: EXTENDED_STAGE.num,
      label: EXTENDED_STAGE.label,
      fields: [
        {
          label: "Removal type",
          value:
            incident.removal_type === "formal_removal"
              ? "Formal removal"
              : incident.removal_type === "administrative_redirect"
                ? "Administrative redirect"
                : null,
        },
        {
          label: "ISS work plan",
          value: incident.iss_work_plan?.trim()
            ? truncate(incident.iss_work_plan.trim(), 200)
            : null,
        },
        {
          label: "Return-to-class plan",
          value: incident.return_to_class_plan?.trim()
            ? truncate(incident.return_to_class_plan.trim(), 200)
            : null,
        },
      ],
    });
  }

  return summaries;
};

// One-line headline used at the top of the card so the leader sees
// the "what we have so far" answer in a single scan even before they
// expand any stage.
export const buildSummaryHeadline = (incident: Incident): string => {
  const name = incident.student_identifier?.trim();
  const grade = incident.grade_level;
  const desc = incident.incident_description?.trim();
  const completion = getStageCompletion(incident);
  const stages = [
    ...STAGE_META,
    ...(showsExtendedStage(incident) ? [EXTENDED_STAGE] : []),
  ];
  const completedCount = stages.filter((s) => completion[s.key]).length;

  if (!name && !desc) return "No incident details captured yet.";

  const subject =
    name && grade
      ? `${name} (Grade ${grade})`
      : name || (grade ? `Grade ${grade}` : "this incident");
  const phase = stages.find((s) => !completion[s.key])?.label ?? "Wrap-up";
  return `${subject} · ${completedCount} of ${stages.length} steps complete · currently on **${phase}**`;
};

// Plain-text version used by the assistant to answer
// "what do we know so far?" / "where am I?" — this is what gets
// rendered as a Bodhi bubble, so it's intentionally compact.
export const buildSummaryDigest = (
  incident: Incident,
  activeStage: ChatTabStage,
): string => {
  const stages = buildStageSummaries(incident);
  const completion = getStageCompletion(incident);
  const allStages = [
    ...STAGE_META,
    ...(showsExtendedStage(incident) ? [EXTENDED_STAGE] : []),
  ];
  const headline = buildSummaryHeadline(incident);

  const lines: string[] = [`**Where we are:** ${headline}`, ""];

  for (const s of stages) {
    const isDone = completion[s.key];
    const isActive = s.key === activeStage;
    const marker = isDone ? "✓" : isActive ? "•" : "○";
    lines.push(
      `${marker} **${s.num}. ${s.label}**${isActive ? " — *current step*" : isDone ? " — *complete*" : ""}`,
    );
    const captured = s.fields.filter((f) => f.value);
    if (captured.length === 0) {
      lines.push("   _Nothing captured yet._");
    } else {
      for (const f of captured) {
        lines.push(`   • ${f.label}: ${f.value}`);
      }
    }
  }

  // Closing nudge — what's the next concrete thing to do?
  const nextStage = allStages.find((s) => !completion[s.key]);
  if (!nextStage) {
    lines.push("", "All four steps are complete. You can review or close the incident from the Resolution tab.");
  } else if (nextStage.key === activeStage) {
    lines.push("", `**Next:** finish *${nextStage.label}* — answer the prompt below to keep moving.`);
  } else {
    lines.push("", `**Next:** *${nextStage.label}*.`);
  }
  return lines.join("\n");
};

// Build a compact one-line preview from a stage's captured fields so the
// row stays a single line by default (e.g. "Joe (Grade 3-5) · IEP/504").
// Returning null tells the row "no preview, just say what's captured/not".
const buildRowPreview = (
  stage: StageSummary,
  isDone: boolean,
): string | null => {
  const captured = stage.fields.filter((f) => f.value).map((f) => f.value as string);
  if (captured.length === 0) return null;
  // Two strongest captured signals, joined; truncate to fit one line.
  const joined = captured.slice(0, 2).join(" · ");
  return joined.length > 80 ? joined.slice(0, 79).trimEnd() + "…" : joined;
};

interface SummaryStageRowProps {
  stage: StageSummary;
  isDone: boolean;
  isActive: boolean;
  isReadOnly: boolean;
  onJump: (stage: ChatTabStage) => void;
  onCloseSelf: () => void;
}

// One stage row inside the expanded summary. Compact by default: status
// icon + label + inline preview + a tiny disclosure caret. Click the row
// (NOT the action button) to expand and see all captured fields.
const SummaryStageRow: React.FC<SummaryStageRowProps> = ({
  stage,
  isDone,
  isActive,
  isReadOnly,
  onJump,
  onCloseSelf,
}) => {
  const [expanded, setExpanded] = useState(false);
  const captured = stage.fields.filter((f) => f.value);
  const preview = buildRowPreview(stage, isDone);
  const hasDetail = captured.length > 0;

  const actionLabel = isActive
    ? "Close & continue"
    : isDone
      ? "Revisit"
      : "Go to step";
  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCloseSelf();
    if (isActive) return;
    onJump(stage.key);
  };

  return (
    <div
      className={cn(
        "rounded-md border bg-white text-xs",
        isActive ? "border-bodhi-teal/40" : "border-gray-200",
      )}
    >
      <button
        type="button"
        onClick={() => hasDetail && setExpanded((v) => !v)}
        disabled={!hasDetail}
        className={cn(
          "w-full flex items-center gap-2 px-2.5 py-1.5 text-left",
          hasDetail && "hover:bg-gray-50",
        )}
      >
        {isDone ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-bodhi-teal flex-shrink-0" aria-hidden />
        ) : (
          <Circle
            className={cn(
              "w-3.5 h-3.5 flex-shrink-0",
              isActive ? "text-bodhi-teal" : "text-gray-300",
            )}
            aria-hidden
          />
        )}
        <span className="font-medium text-gray-900 flex-shrink-0">
          {stage.num}. {stage.label}
        </span>
        {isActive && (
          <span className="text-[9px] uppercase tracking-wide text-bodhi-teal/80 bg-bodhi-teal/10 rounded px-1 py-0.5 flex-shrink-0">
            Current
          </span>
        )}
        {preview ? (
          <span className="text-gray-500 truncate min-w-0 flex-1">
            {preview}
          </span>
        ) : (
          <span className="text-gray-400 italic truncate min-w-0 flex-1">
            Not captured yet
          </span>
        )}
        {!isReadOnly && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleAction}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleAction(e as unknown as React.MouseEvent);
            }}
            className="text-[11px] text-bodhi-teal hover:underline flex-shrink-0 ml-1"
          >
            {actionLabel}
          </span>
        )}
        {hasDetail && (
          <ChevronDown
            className={cn(
              "w-3 h-3 text-gray-400 flex-shrink-0 transition-transform",
              expanded && "rotate-180",
            )}
            aria-hidden
          />
        )}
      </button>
      {expanded && hasDetail && (
        <ul className="border-t border-gray-100 px-2.5 py-1.5 space-y-0.5">
          {captured.map((f) => (
            <li key={f.label} className="flex flex-col sm:flex-row sm:gap-2">
              <span className="text-gray-500 sm:w-32 sm:flex-shrink-0 text-[11px]">
                {f.label}
              </span>
              <span className="text-gray-800 break-words flex-1 text-[11px]">
                {f.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface IncidentSummaryCardProps {
  incident: Incident;
  activeStage: ChatTabStage;
  onJump: (stage: ChatTabStage) => void;
  onContinue: () => void;
  // True the first time the chat boots into an existing (resumed) incident.
  // We default the card to expanded in that case so the leader immediately
  // sees what's already on the record.
  defaultOpen?: boolean;
  isReadOnly?: boolean;
}

export const IncidentSummaryCard: React.FC<IncidentSummaryCardProps> = ({
  incident,
  activeStage,
  onJump,
  onContinue,
  defaultOpen = false,
  isReadOnly = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const summaries = useMemo(() => buildStageSummaries(incident), [incident]);
  const completion = useMemo(() => getStageCompletion(incident), [incident]);
  const allStages = useMemo(
    () => [
      ...STAGE_META,
      ...(showsExtendedStage(incident) ? [EXTENDED_STAGE] : []),
    ],
    [incident],
  );
  const completedCount = allStages.filter((s) => completion[s.key]).length;
  const totalSteps = allStages.length;

  const headline = useMemo(() => buildSummaryHeadline(incident), [incident]);
  const isLoading = !incident.id;

  return (
    <div className="border-b border-gray-200 bg-gradient-to-b from-bodhi-teal/[0.04] to-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-bodhi-teal/[0.06] transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {open ? (
            <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
          )}
          <span className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold flex-shrink-0">
            What we have so far
          </span>
          {isLoading ? (
            <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin flex-shrink-0" aria-hidden />
          ) : (
            <span
              className="text-xs text-gray-700 truncate"
              dangerouslySetInnerHTML={{
                __html: headline.replace(
                  /\*\*(.+?)\*\*/g,
                  '<strong class="text-gray-900">$1</strong>',
                ),
              }}
            />
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] text-gray-500 hidden sm:inline">
            {completedCount}/{totalSteps}
          </span>
          {!isReadOnly && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onContinue();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(false);
                  onContinue();
                }
              }}
              className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-bodhi-teal hover:text-bodhi-teal/80 hover:underline"
            >
              Continue
              <ArrowRight className="w-3.5 h-3.5" aria-hidden />
            </span>
          )}
        </div>
      </button>

      {open && (
        // Hard-cap the panel so even with all stages expanded, the leader
        // still has roughly two-thirds of the chat column visible. The
        // panel scrolls internally if content exceeds the cap.
        <div className="px-3 pb-3 pt-0.5 space-y-1 max-h-[28vh] overflow-y-auto">
          {summaries.map((s) => (
            <SummaryStageRow
              key={s.key}
              stage={s}
              isDone={completion[s.key]}
              isActive={s.key === activeStage}
              isReadOnly={isReadOnly}
              onJump={(stage) => {
                setOpen(false);
                onJump(stage);
              }}
              onCloseSelf={() => setOpen(false)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
